import { useTranslation } from '@kostbar/i18n'
import type { CustomRange, PeriodGrain } from '@/features/reports/lib/period'
import {
  endOfReportMonth,
  isoWeekOf,
  shiftReportMonth,
  startOfReportMonth,
  startOfReportWeek,
} from '@/features/reports/lib/period'
import { reportDate, shiftReportDate } from '@/features/reports/lib/report-zone'
import { formatDate } from '@/features/reports/lib/format'
import { WEEKDAY_SHORT } from '@/features/reports/lib/dimension-labels'
import { cn } from '@/lib/utils'

interface PeriodGrainGridProps {
  grain: PeriodGrain
  /** `YYYY-MM-DD` inside the month (days, weeks) or year (months, quarters) being paged through. */
  cursor: string
  value: CustomRange | null
  /** The half-picked day on the day grid: set by the first click, cleared by the second. */
  anchor: string | null
  onPick: (range: CustomRange, anchor: string | null) => void
  today: string
}

/**
 * The grid behind all four levels of the period picker (CHG-2026-09-smarter-period-picker).
 *
 * The **level switch says what a click selects** — a day, a week, a month, a quarter — so the
 * target is the period itself: a row labelled "KW 36", a tile labelled "Sep". A week is not a day
 * that happens to imply one, and picking a month needs no second control.
 *
 * The day grid is hand-rolled rather than react-day-picker's, so all four levels share one pager,
 * one selection model and one look. The library's calendar brought its own caption, its own
 * navigation and its own idea of when a range is complete — three things to keep in step with the
 * other three levels, for a month grid that is 42 buttons.
 *
 * Everything is `YYYY-MM-DD` on the Vienna calendar, never a `Date`, so nothing here can drift by a
 * day when the viewer sits in another timezone.
 */
export function PeriodGrainGrid({
  grain,
  cursor,
  value,
  anchor,
  onPick,
  today,
}: PeriodGrainGridProps) {
  const { t } = useTranslation('reports')

  if (grain === 'day') {
    return (
      <div className="p-3">
        <div className="mb-1 grid grid-cols-7 gap-0.5">
          {WEEKDAY_SHORT.map((name) => (
            <div
              key={name}
              className="py-1 text-center text-[11px] font-semibold text-muted-foreground"
            >
              {name}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {daysOfMonthGrid(cursor).map((day) => {
            const future = day > today
            const outside = !day.startsWith(cursor.slice(0, 7))
            const from = value?.from
            const to = value?.to
            const edge = day === from || day === to
            const inside =
              from != null && to != null && day >= from && day <= to
            return (
              <button
                key={day}
                type="button"
                // The cell's own date, so a test can name the day it clicks: the visible label is
                // just a number and September's 2nd is not the only "2" in a six-week grid.
                data-day={day}
                disabled={future}
                onClick={() => {
                  const [range, nextAnchor] = pickDay(day, anchor)
                  onPick(range, nextAnchor)
                }}
                className={cn(
                  'h-9 rounded-md text-sm tabular-nums sm:h-8',
                  edge
                    ? 'bg-primary font-semibold text-primary-foreground'
                    : inside
                      ? 'bg-accent'
                      : 'hover:bg-accent',
                  // Squared off on the inner side, so a picked range reads as one bar rather than
                  // as separate pills with gaps between them.
                  day === from && day !== to && 'rounded-r-none',
                  day === to && day !== from && 'rounded-l-none',
                  inside && !edge && 'rounded-none',
                  !inside && outside && 'text-muted-foreground/60',
                  future &&
                    'cursor-not-allowed opacity-40 hover:bg-transparent',
                )}
              >
                {Number(day.slice(8, 10))}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  if (grain === 'week') {
    return (
      <div className="flex flex-col gap-0.5 p-3">
        {weekWindow(cursor).map((week) => {
          const active = value?.from === week.from && value.to === week.to
          const future = week.from > today
          return (
            <button
              key={week.from}
              type="button"
              disabled={future}
              onClick={() => onPick({ ...week, grain: 'week' }, null)}
              className={cn(
                'flex h-11 items-center gap-3 rounded-lg px-3 text-left text-sm sm:h-9',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-accent',
                future && 'cursor-not-allowed opacity-40 hover:bg-transparent',
              )}
            >
              <span
                className={cn(
                  'w-14 shrink-0 text-xs font-semibold tabular-nums',
                  active
                    ? 'text-primary-foreground/70'
                    : 'text-muted-foreground',
                )}
              >
                {t('period.weekShort', { week: isoWeekOf(week.from) })}
              </span>
              <span className="tabular-nums">
                {formatDate(`${week.from}T12:00:00Z`)} –{' '}
                {formatDate(`${week.to}T12:00:00Z`)}
              </span>
            </button>
          )
        })}
      </div>
    )
  }

  const year = cursor.slice(0, 4)
  if (grain === 'month') {
    return (
      <div className="grid grid-cols-4 gap-1.5 p-3">
        {Array.from({ length: 12 }, (_, index) => {
          const from = `${year}-${String(index + 1).padStart(2, '0')}-01`
          const to = endOfReportMonth(from)
          const active = value?.from === from && value.to === to
          const future = from > today
          return (
            <button
              key={from}
              type="button"
              disabled={future}
              onClick={() => onPick({ from, to, grain: 'month' }, null)}
              className={cn(
                'h-11 rounded-lg text-sm sm:h-9',
                active
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : 'hover:bg-accent',
                future && 'cursor-not-allowed opacity-40 hover:bg-transparent',
              )}
            >
              {t(`period.monthsShort.${index}`)}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-1.5 p-3">
      {Array.from({ length: 4 }, (_, index) => {
        const from = `${year}-${String(index * 3 + 1).padStart(2, '0')}-01`
        const to = endOfReportMonth(from, 2)
        const active = value?.from === from && value.to === to
        const future = from > today
        return (
          <button
            key={from}
            type="button"
            disabled={future}
            onClick={() => onPick({ from, to, grain: 'quarter' }, null)}
            className={cn(
              'flex h-14 flex-col items-center justify-center gap-0.5 rounded-lg text-sm',
              active
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'hover:bg-accent',
              future && 'cursor-not-allowed opacity-40 hover:bg-transparent',
            )}
          >
            <span>{t('period.quarter', { quarter: index + 1 })}</span>
            <span
              className={cn(
                'text-xs',
                active ? 'text-primary-foreground/70' : 'text-muted-foreground',
              )}
            >
              {t(`period.monthsShort.${index * 3}`)} –{' '}
              {t(`period.monthsShort.${index * 3 + 2}`)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * What a click on a day selects: the first starts a range of one day and leaves an anchor, the
 * second closes it in whichever direction it was dragged.
 *
 * The anchor is what a plain "selected range" cannot express, and its absence was a real defect:
 * react-day-picker reports the very first click as a *complete* range, so a two-click pick applied
 * as a single day unless the two clicks happened to land inside its own window.
 */
export function pickDay(
  day: string,
  anchor: string | null,
): [CustomRange, string | null] {
  if (anchor == null) {
    return [{ from: day, to: day, grain: 'day' }, day]
  }
  const [from, to] = day < anchor ? [day, anchor] : [anchor, day]
  return [{ from, to, grain: 'day' }, null]
}

/** Six Monday-to-Sunday weeks covering the month `cursor` falls in — 42 day cells. */
export function daysOfMonthGrid(cursor: string): Array<string> {
  const start = startOfReportWeek(startOfReportMonth(cursor))
  return Array.from({ length: 42 }, (_, index) => shiftReportDate(start, index))
}

/**
 * The six Monday-to-Sunday weeks **ending** with the week `cursor` falls in.
 *
 * Deliberately not the six weeks of `cursor`'s calendar month. A month grid opened on today shows
 * one usable row and four greyed-out future ones — and a report is almost always about a week that
 * has already happened. This window is the recent past, with the running week last.
 */
export function weekWindow(
  cursor: string,
): Array<{ from: string; to: string }> {
  const last = startOfReportWeek(cursor)
  return Array.from({ length: 6 }, (_, index) => {
    const from = shiftReportDate(last, (index - 5) * 7)
    return { from, to: shiftReportDate(from, 6) }
  })
}

/** The month or year label above the grid, and how far it may be paged. */
export function pageCursor(
  cursor: string,
  grain: PeriodGrain,
  direction: -1 | 1,
): string {
  if (grain === 'month' || grain === 'quarter') {
    return shiftReportMonth(cursor, direction * 12)
  }
  // Weeks page by a whole window, so no week is shown twice and none is skipped; days by a month,
  // which is exactly what their grid shows.
  return grain === 'week'
    ? shiftReportDate(cursor, direction * 42)
    : shiftReportMonth(cursor, direction)
}

/** True when paging forward would leave the shown period entirely in the future. */
export function pageIsAtPresent(
  cursor: string,
  grain: PeriodGrain,
  now: Date,
): boolean {
  const today = reportDate(now)
  if (grain === 'month' || grain === 'quarter') {
    return cursor.slice(0, 4) >= today.slice(0, 4)
  }
  return grain === 'week'
    ? startOfReportWeek(cursor) >= startOfReportWeek(today)
    : cursor.slice(0, 7) >= today.slice(0, 7)
}
